"use client"

import { Copy } from "@/app/components/copy/copy"
import { Trans, useLingui } from "@lingui/react/macro"
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { CircleDollarSign, CreditCard } from "lucide-react"

export const GachaBalanceDialogView = reatomComponent<{
  balanceAmount: string
  walletAddress: string
  tokenSymbol: string
  tokenIcon?: string
  networkName: string
  onDeposit?: () => void
}>(
  ({
    balanceAmount,
    walletAddress,
    tokenSymbol,
    tokenIcon,
    networkName,
    onDeposit,
  }) => {
    const { t } = useLingui()

    return (
      <DialogContent className="max-w-[440px] gap-5 p-5">
        <DialogHeader>
          <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
            <Trans>Balance</Trans>
          </DialogTitle>
        </DialogHeader>

        <p className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-xs leading-5 text-foreground/80">
          <Trans>
            Deposit {tokenSymbol} on {networkName} only. Funds on other networks
            are not available for this balance.
          </Trans>
        </p>

        <div className="flex flex-col gap-3 rounded-lg border border-foreground/10 bg-foreground/[0.02] p-4">
          <div className="flex items-center gap-3">
            <GachaPaymentTokenIcon
              symbol={tokenSymbol}
              icon={tokenIcon}
              className="h-10 w-10"
            />
            <div className="min-w-0 flex-1 break-words font-headline text-2xl text-foreground">
              {balanceAmount} <span>{tokenSymbol}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-foreground/10 p-4">
          <p className="text-xs text-foreground/60">
            <Trans>Wallet address</Trans>
          </p>
          <Copy
            copyValue={walletAddress}
            label={t`Copy address`}
            tooltipOnlyOnCopy
            withIcon
            className="shrink-0"
          >
            <GachaWalletAddressLabel
              address={walletAddress}
              className="text-sm text-foreground"
            />
          </Copy>
        </div>

        <Button
          type="button"
          variant="secondary"
          className="h-auto w-full justify-start gap-3 px-4 py-3"
          onClick={onDeposit}
          disabled={!onDeposit}
        >
          <CreditCard
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-foreground/80"
          />
          <span className="text-sm">
            <Trans>Deposit via debit / credit card</Trans>
          </span>
        </Button>
        {!onDeposit && (
          <p className="text-xs text-foreground/60">
            <Trans>
              Card deposits are not available for this network and token.
              Transfer funds to the wallet address above instead.
            </Trans>
          </p>
        )}
      </DialogContent>
    )
  },
  "GachaBalanceDialogView",
)

const GachaWalletAddressLabel = reatomComponent<{
  address: string
  className?: string
}>(({ address, className }) => {
  if (address.length <= 10) {
    return <span className={className}>{address}</span>
  }

  return (
    <span className={cn("inline-flex items-center", className)}>
      <span className="shrink-0">{address.slice(0, 5)}</span>
      <span
        className="inline-flex shrink-0 items-center gap-[2px] px-0.5 [font-feature-settings:'liga'_0,'calt'_0]"
        aria-hidden="true"
      >
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
      <span className="shrink-0">{address.slice(-5)}</span>
    </span>
  )
}, "GachaWalletAddressLabel")

export const GachaPaymentTokenIcon = reatomComponent<{
  symbol: string
  icon?: string
  className?: string
}>(({ symbol, icon, className }) => {
  if (icon) {
    return (
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        className={cn("shrink-0", className)}
      />
    )
  }
  if (symbol !== "USDC" && symbol !== "tUSDC") {
    return (
      <CircleDollarSign
        aria-hidden="true"
        className={cn("shrink-0", className)}
      />
    )
  }

  return (
    <svg
      viewBox="0 0 96 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("shrink-0", className)}
      aria-hidden="true"
    >
      <path
        d="M48 95C73.9574 95 95 73.9574 95 48C95 22.0426 73.9574 1 48 1C22.0426 1 1 22.0426 1 48C1 73.9574 22.0426 95 48 95Z"
        fill="#0B53BF"
      />
      <path
        d="M56.4609 13.7778V19.8291C68.5341 23.4716 77.3759 34.6928 77.3759 47.9997C77.3759 61.3066 68.5341 72.5278 56.4609 76.1703V82.2216C71.8534 78.4616 83.2509 64.5672 83.2509 47.9997C83.2509 31.4322 71.8534 17.5378 56.4609 13.7778Z"
        fill="white"
      />
      <path
        d="M18.625 47.9997C18.625 34.6928 27.4669 23.4716 39.54 19.8291V13.7778C24.1475 17.5378 12.75 31.4322 12.75 47.9997C12.75 64.5672 24.1475 78.4616 39.54 82.2216V76.1703C27.4669 72.5572 18.625 61.3066 18.625 47.9997Z"
        fill="white"
      />
      <path
        d="M60.6319 54.5506C60.6319 42.5362 41.8025 47.4713 41.8025 40.8325C41.8025 38.4531 43.7119 36.9256 47.3544 36.9256C51.7019 36.9256 53.2 39.0406 53.67 41.89H59.6625C59.1279 36.5426 56.0588 33.1662 50.9382 32.1604V27.4375H45.0632V31.9918C39.4534 32.7062 35.9275 35.973 35.9275 40.8325C35.9275 52.9056 54.7863 48.3819 54.7863 54.9031C54.7863 57.3706 52.4069 59.0156 48.3825 59.0156C43.1244 59.0156 41.3913 56.695 40.745 53.4931H34.8994C35.2781 59.3502 38.8897 63.0159 45.0632 63.9307V68.5625H50.9382V63.9923C56.9633 63.2139 60.6319 59.7089 60.6319 54.5506Z"
        fill="white"
      />
    </svg>
  )
}, "GachaPaymentTokenIcon")
